import { InventoryItemFragment } from '@epicuro-next/data/product';
import {
  selectIsOperableStatus,
  selectOperableExtendedBasketItems,
} from '@epicuro-next/state/basket-state';
import {
  selectAdvancedMDOAvailableAndEnabled,
  selectAdvancedMDOSizesConfigAvailable,
} from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';

import { DisplayByMode } from '../../models';
import { selectDisableOnBasketUpdateRule } from '../config-strip-configuration.selectors';
import { selectConfigStripState } from '../config-strip.feature.selector';
import {
  selectDeliveryShopsCount,
  selectProduct,
} from '../data/config-strip.data.selectors';

export type CellInventory = {
  totals: number;
  inventories: InventoryItemFragment[];
  hasTrueDemandInventory: boolean;
};

export const selectUIConfigStripState = createSelector(
  selectConfigStripState,
  (state) => state?.ui,
);

export const selectIsConfigStripOpen = createSelector(
  selectUIConfigStripState,
  (state) => Boolean(state) && state.isOpen,
);

export const selectProductLoadingFailed = createSelector(
  selectUIConfigStripState,
  (state) => state.error,
);

export const selectProductLoading = createSelector(
  selectUIConfigStripState,
  (state) => state.loading,
);

export const selectProductResolvedFromCached = createSelector(
  selectUIConfigStripState,
  (state) => state.loadedCached,
);

export const selectInitialSelectedVariantId = createSelector(
  selectUIConfigStripState,
  (state) => state.initialSelectedVariant,
);

export const selectCurrentlySelectedVariantId = createSelector(
  selectUIConfigStripState,
  (state) => state.selectedVariant,
);

export const selectInitiallySelectedPrepackId = createSelector(
  selectUIConfigStripState,
  (state) => state.initialSelectedPrepack,
);

export const selectCurrentlySelectedPrepackId = createSelector(
  selectUIConfigStripState,
  (state) => state.selectedPrepack ?? state.initialSelectedPrepack,
);

export const selectCurrentlySelectedSizeId = createSelector(
  selectUIConfigStripState,
  (state) => state.selectedSize,
);

export const selectCopiedDeliveryPeriods = createSelector(
  selectUIConfigStripState,
  (state) => state.copiedDeliveryPeriods,
);

export const selectCurrentlyActiveOrderType = createSelector(
  selectUIConfigStripState,
  (state) => state.selectedOrderType,
);

export const selectPastedShops = createSelector(
  selectUIConfigStripState,
  (state) =>
    state.copiedShops.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}),
);

export const selectPastedData = createSelector(
  selectUIConfigStripState,
  (state) => state.pastedData,
);

export const selectPreSelectedDeliveryPeriodType = createSelector(
  selectUIConfigStripState,
  (state) => state.preSelectedDeliveryPeriodType,
);
export const selectDisplayByMode = createSelector(
  selectUIConfigStripState,
  selectAdvancedMDOAvailableAndEnabled,
  selectAdvancedMDOSizesConfigAvailable,
  selectDeliveryShopsCount,
  (
    state,
    advancedMDOAvailableAndEnabled,
    advancedMDOSizesConfigAvailable,
    shopsCount,
  ) => {
    if (advancedMDOAvailableAndEnabled && shopsCount > 1) {
      if (state.displayByMode === DisplayByMode.Prepack) {
        return DisplayByMode.AdvancedPrepack;
      }

      if (
        state.displayByMode === DisplayByMode.Size &&
        advancedMDOSizesConfigAvailable
      ) {
        return DisplayByMode.AdvancedSize;
      }
    }

    return state.displayByMode;
  },
);

export const selectAdvancedMdoPrepacks = createSelector(
  selectDisplayByMode,
  (displayByMode: DisplayByMode) =>
    displayByMode === DisplayByMode.AdvancedPrepack,
);

export const selectAdvancedMdoSizes = createSelector(
  selectDisplayByMode,
  (displayByMode: DisplayByMode) =>
    displayByMode === DisplayByMode.AdvancedSize,
);

export const selectAdvancedMdo = createSelector(
  selectAdvancedMdoPrepacks,
  selectAdvancedMdoSizes,
  (advancedMdoPrepacks, advancedMdoSizes) =>
    advancedMdoPrepacks || advancedMdoSizes,
);

export const selectValidating = createSelector(
  selectUIConfigStripState,
  (state) => state.validating,
);

export const selectValidationErrors = createSelector(
  selectUIConfigStripState,
  (state) => state.validationErrors,
);

export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (storeErrors) =>
    Object.values(storeErrors)
      .filter((v) => v != null)
      .map((periodErrors) => Object.values(periodErrors))
      // flat(2) because it's 2 level deep: store level => period level => errors
      .flat(2)
      .filter((v) => v != null).length > 0,
);

export const selectPreselectedDeliveryPeriod = createSelector(
  selectUIConfigStripState,
  (state) => state.preSelectedDeliveryPeriod,
);

export const selectPreSelectedOrderType = createSelector(
  selectUIConfigStripState,
  (state) => state.preSelectedOrderType,
);

export const selectPricesLoading = createSelector(
  selectUIConfigStripState,
  (state) => state.pricesLoading,
);

export const selectEcommerceTrackingSource = createSelector(
  selectUIConfigStripState,
  (state) => state.ecommerceTrackingSource,
);

export const selectBasketItemsForProduct = createSelector(
  selectProduct,
  selectOperableExtendedBasketItems,
  (product, operableExtendedBasketItems) => {
    return operableExtendedBasketItems.filter(
      (bi) => bi.articleId === product?.productId,
    );
  },
);

export const selectIsUpdatingCart = createSelector(
  selectBasketItemsForProduct,
  (basketItemsForProduct) => {
    return (
      basketItemsForProduct !== undefined && basketItemsForProduct.length > 0
    );
  },
);

export const selectIsDisabledOnBasketUpdate = createSelector(
  selectDisableOnBasketUpdateRule,
  selectIsOperableStatus,
  (isDisableOnBasketUpdate, isOperableStatus) => {
    return isDisableOnBasketUpdate && !isOperableStatus;
  },
);
